import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';

import useStyles from '../../../useStyles';
import BottomEdgeDivider from './BottomEdgeDivider';

const useLayoutStyles = makeStyles((theme) => ({
    topSpreadRow: {
        justifyContent: 'space-between',
        width: '100%',
        boxSizing: 'border-box',
        gap: theme.spacing(1),
    },
    titleLead: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    titleLeadHeading: {
        flex: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    trailingSlot: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
    },
    leadingSlot: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
    },
    headingBlock: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
    },
    headingText: {
        flex: '0 1 auto',
        minWidth: 0,
    },
    subtitle: {
        margin: 0,
        flex: '0 0 auto',
    },
}));

const AppPageTitle = ({
    title,
    subtitle,
    icon,
    align = 'center',
    dividerInsetX = 16,
    dividerColor = '#000000',
    dividerThickness = 1,
    hideDivider = false,
    disableDefaultStyles = false,
    className,
    topClassName,
    titleClassName,
    subtitleClassName,
    iconWrapClassName,
    dividerWrapClassName,
    leadingAction,
    trailingAction,
}) => {
    const classes = useStyles();
    const layoutClasses = useLayoutStyles();
    const useSpreadTopRow = Boolean(icon) || Boolean(leadingAction) || Boolean(trailingAction);
    const rootClassName = [disableDefaultStyles ? '' : classes.appPageTitleRoot, className || ''].filter(Boolean).join(' ');
    const resolvedTopClassName = [
        disableDefaultStyles ? '' : classes.appPageTitleTop,
        !disableDefaultStyles && align === 'start' ? classes.appPageTitleTopStart : '',
        topClassName || '',
        useSpreadTopRow ? layoutClasses.topSpreadRow : '',
    ].filter(Boolean).join(' ');
    const resolvedTitleClassName = [
        disableDefaultStyles ? '' : classes.appPageTitleText,
        !disableDefaultStyles && align === 'start' ? classes.appPageTitleTextStart : '',
        titleClassName || '',
        useSpreadTopRow ? layoutClasses.titleLeadHeading : '',
    ].filter(Boolean).join(' ');
    const resolvedIconWrapClassName = [disableDefaultStyles ? '' : classes.appPageTitleIconWrap, iconWrapClassName || ''].filter(Boolean).join(' ');
    const resolvedDividerWrapClassName = [disableDefaultStyles ? '' : classes.appPageTitleDividerWrap, dividerWrapClassName || ''].filter(Boolean).join(' ');
    const resolvedSubtitleClassName = [layoutClasses.subtitle, subtitleClassName || ''].filter(Boolean).join(' ');

    const headingBlock = subtitle ? (
        <Box className={layoutClasses.headingBlock}>
            <h2 className={[resolvedTitleClassName, layoutClasses.headingText].filter(Boolean).join(' ')}>{title}</h2>
            <span className={resolvedSubtitleClassName}>{subtitle}</span>
        </Box>
    ) : (
        <h2 className={resolvedTitleClassName}>{title}</h2>
    );

    return (
        <Box className={rootClassName}>
            <Box className={resolvedTopClassName}>
                {useSpreadTopRow ? (
                    <>
                        <Box className={layoutClasses.titleLead}>
                            {leadingAction ? <Box className={layoutClasses.leadingSlot}>{leadingAction}</Box> : null}
                            {headingBlock}
                            {icon ? <Box className={resolvedIconWrapClassName}>{icon}</Box> : null}
                        </Box>
                        {trailingAction ? <Box className={layoutClasses.trailingSlot}>{trailingAction}</Box> : null}
                    </>
                ) : (
                    headingBlock
                )}
            </Box>
            {!hideDivider ? (
                <Box className={resolvedDividerWrapClassName}>
                    <BottomEdgeDivider insetX={dividerInsetX} thickness={dividerThickness} color={dividerColor} />
                </Box>
            ) : null}
        </Box>
    );
};

AppPageTitle.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    icon: PropTypes.node,
    align: PropTypes.oneOf(['center', 'start']),
    dividerInsetX: PropTypes.number,
    dividerColor: PropTypes.string,
    dividerThickness: PropTypes.number,
    hideDivider: PropTypes.bool,
    disableDefaultStyles: PropTypes.bool,
    className: PropTypes.string,
    topClassName: PropTypes.string,
    titleClassName: PropTypes.string,
    subtitleClassName: PropTypes.string,
    iconWrapClassName: PropTypes.string,
    dividerWrapClassName: PropTypes.string,
    leadingAction: PropTypes.node,
    trailingAction: PropTypes.node,
};

export default AppPageTitle;
